import { Stack } from "@mantine/core";
import { forwardRef, memo } from "react";
import type { ChatMessage } from "@/sdk";
import { AuthorBadge } from "./AuthorBadge";
import { renderMessageTypeExtraComponent } from "./CustomMessages/messageTypeRenderers";
import { MessageBubble } from "./MessageBubble";
import { MessageContent } from "./MessageContent";

interface MessageListItemProps {
    firstOfAuthorGroup: boolean;
    lastOfAuthorGroup: boolean;
    message: ChatMessage;
    sessionId: string;
    customerName: string; //TODO: add friendlyNames to each message in the store instead
    lastMessageInGroupTime?: Date | null; // Time of the last message in the current author's group
}

export const MessageListItem = memo(
    forwardRef<HTMLDivElement | null, MessageListItemProps>(
        ({ message, firstOfAuthorGroup, lastOfAuthorGroup, sessionId, customerName, lastMessageInGroupTime }, ref) => {
            //FIXME: senderType !== "expert" won't work for transfers/chats with multiple experts https://solutonashville.atlassian.net/browse/EWPM-58

            //TODO: css module rules instead with https://soluto.atlassian.net/browse/EWPM-56
            const flexAlign = message.senderType === "customer" ? "flex-start" : "flex-end";
            const flexProps = { align: flexAlign, justify: flexAlign, mb: lastOfAuthorGroup ? "lg" : "xs", gap: "xs" };

            /** Use the time of the last message in the group (if provided) or fall back to current message time */
            const badgeDisplayTime =
                firstOfAuthorGroup && lastMessageInGroupTime ? lastMessageInGroupTime : message.dateCreated;

            return (
                <Stack {...flexProps} ref={ref}>
                    {firstOfAuthorGroup && (
                        <AuthorBadge
                            senderType={message.senderType}
                            senderId={message.senderId}
                            customerName={customerName}
                            sessionId={sessionId}
                            dateCreated={badgeDisplayTime}
                        />
                    )}
                    {message.body && (
                        <MessageBubble
                            lastOfGroup={lastOfAuthorGroup}
                            senderType={message.senderType}
                            flexAlign={flexAlign}
                            messageTime={message.dateCreated}
                        >
                            <MessageContent
                                content={message.body}
                                showReceipt={message.senderType === "expert"}
                                receiptStatus={message.receiptStatus}
                            />
                        </MessageBubble>
                    )}
                    {renderMessageTypeExtraComponent(message)}
                </Stack>
            );
        },
    ),
);

MessageListItem.displayName = "MessageListItem"; // Required for forwardRef components
